import { useState } from 'react';
import axios from 'axios';
import { API } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';

export default function AddContactDialog({ open, onClose, onContactCreated }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    status: 'active',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/contacts`, formData);
      onContactCreated(response.data);
      toast.success('Contact added');
      setFormData({ name: '', email: '', phone: '', company: '', status: 'active' });
    } catch (error) {
      toast.error('Failed to add contact');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">New Contact</DialogTitle>
          <DialogDescription>Add someone you don't want to forget.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-700 font-medium">
              Name
            </Label>
            <Input
              id="name"
              data-testid="dialog-contact-name-input"
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
              placeholder="John Doe"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-700 font-medium">
              Email (optional)
            </Label>
            <Input
              id="email"
              data-testid="dialog-contact-email-input"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
              placeholder="john@example.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-gray-700 font-medium">
              Phone (optional)
            </Label>
            <Input
              id="phone"
              data-testid="dialog-contact-phone-input"
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
              placeholder="(555) 123-4567"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="company" className="text-gray-700 font-medium">
              Company (optional)
            </Label>
            <Input
              id="company"
              data-testid="dialog-contact-company-input"
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="h-11 rounded-lg border-gray-200 bg-gray-50 focus:bg-white"
              placeholder="Acme Inc."
            />
          </div>
          <div className="flex gap-3 pt-4">
            <Button
              data-testid="dialog-create-contact-button"
              type="submit"
              disabled={loading}
              className="flex-1 h-11 rounded-full bg-[#1A4D2E] hover:bg-[#14532D] text-white"
            >
              {loading ? 'Adding...' : 'Save Contact'}
            </Button>
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              className="rounded-full px-6"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}